import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import type { ExecutionOutput } from '@/hooks/useRealtimeWorkflows';
import { Download, ChevronLeft, ChevronRight, X, File as FileIcon, Loader2, Copy, Check } from 'lucide-react';

export type OutputKind = 'image' | 'video' | 'text' | 'other';

export function getOutputKind(filename: string): OutputKind {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  if (['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'mov', 'ogg'].includes(ext)) return 'video';
  if (ext === 'txt') return 'text';
  return 'other';
}

interface OutputViewerProps {
  output: ExecutionOutput;
  name: string;
  index: number;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onDownload: (url: string, downloadName: string) => void;
}

export default function OutputViewer({ output, name, index, total, onClose, onPrev, onNext, onDownload }: OutputViewerProps) {
  const kind = getOutputKind(output.filename);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [textError, setTextError] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (textContent === null) return;
    try {
      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  // Load .txt content for inline preview.
  useEffect(() => {
    if (kind !== 'text') return;
    let cancelled = false;
    setTextContent(null);
    setTextError(false);
    fetch(output.url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setTextContent(text);
      })
      .catch(() => {
        if (!cancelled) setTextError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [output.url, kind]);

  return (
    <div
      className="fixed left-0 right-0 bottom-0 top-10 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        title="Close"
        className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          title="Previous"
          className="absolute left-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Content */}
      <div className="max-w-5xl max-h-[85vh] w-full mx-16 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-full flex-1 min-h-0 flex items-center justify-center">
          {kind === 'image' && <img src={output.url} alt={name} className="max-w-full max-h-[75vh] object-contain rounded-lg" />}
          {kind === 'video' && <video src={output.url} controls autoPlay className="max-w-full max-h-[75vh] rounded-lg" />}
          {kind === 'text' && (
            <div className="relative w-full max-h-[75vh] overflow-auto rounded-lg bg-background p-4">
              {textError ? (
                <p className="text-sm text-destructive">Failed to load file content.</p>
              ) : textContent === null ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCopy}
                    title={copied ? 'Copied!' : 'Copy to clipboard'}
                    className="sticky top-0 float-right ml-2 mb-2"
                  >
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                  <pre className="text-sm text-foreground whitespace-pre-wrap break-words font-mono">{textContent}</pre>
                </>
              )}
            </div>
          )}
          {kind === 'other' && (
            <div className="flex flex-col items-center gap-4 rounded-lg bg-background p-10">
              <FileIcon className="h-16 w-16 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
              <Button
                size="sm"
                onClick={() => onDownload(output.url, name)}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          )}
        </div>
        <div className="mt-3 flex items-center gap-3 text-white/80">
          <span className="text-sm truncate max-w-md">{name}</span>
          {total > 1 && (
            <span className="text-xs text-white/50">
              {index + 1} / {total}
            </span>
          )}
          <Button size="sm" onClick={() => onDownload(output.url, name)} className="bg-violet-600 hover:bg-violet-700 text-white">
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          title="Next"
          className="absolute right-4 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
